import { axios } from '@/plugins/axios';
import { Resource, Pagination, Statement } from "@/helpers/Interfaces";
import Pager from "@/helpers/Pager";
import { merge } from "lodash";
import { DepartmentEnum } from '@/modules/renter/tasks/services/TasksService';
import type { ListItemModel as DistrictModel } from '@/modules/renter/districts/services/DistrictsService';
import type { FilterModel } from '../types';

export default class ChecklistsService
{
    public static async create(form: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/checklists`, form)).data;
    }

    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/checklists/${publicId}`)).data;
    }

    public static async fetchWithMatchedEquipment(publicId: string, propertyPublicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/checklists/${publicId}/${propertyPublicId}`)).data;
    }

    public static async update(publicId: string, form: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.put<Resource<FormModel>>(`admin/renters/checklists/${publicId}`, form)).data;
    }

    public static async getEquipmentsList(): Promise<EquipmentItem[]>
    {
        return (await axios.get(`admin/renters/equipment/autocomplete`)).data;
    }

    public static async getEquipment(publicId: string): Promise<EquipmentItem>
    {
        return (await axios.get(`admin/renters/equipment/autocomplete${publicId}`)).data;
    }

    public static async getList(pager: Pager, filter: FilterModel): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/checklists', {
            params: merge(filter, pager.data())
        })).data;
    }

    public static async remove(publicId: string): Promise<Statement>
    {
        return (await axios.delete(`admin/renters/checklists/${publicId}`)).data;
    }

    public static async copy(publicId: string): Promise<Statement>
    {
        return (await axios.post(`admin/renters/checklists/copy/${publicId}`)).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 5, department: DepartmentEnum = DepartmentEnum.Cleaning, cityId: number): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/renters/checklists/autocomplete', {
            params: {
                search: search,
                limit: limit,
                department,
                cityId
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/checklists/autocomplete/${publicId}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    publicId: string;
    name: string;
    description: string;
    department: string;
    cityId: number;
    cities: string[]
}

export interface ChecklistItem
{
    equipmentId: number | string;
    equipment: EquipmentItem;
    tasks: string[];
    position: number;
    edit: boolean;
}

export interface EquipmentItem
{
    name: string;
    avantioId: number;
    publicID: string;
    id: number;
}

export interface FormModel
{
    name: string;
    description: string;
    department: DepartmentEnum;
    checklistItems: Array<ChecklistItem>;
    defaultTimeUtc: {
        hour: number,
        minute: number
    };
    assignedCities: City[];
    assignedDistricts: DistrictModel[],
    isAutoPlanned: boolean,
    isAfterBlockade: boolean
}

export interface City
{
    id: number,
    publicId: string,
    name: string
}

export interface ChecklistItemModel extends ListItemModel {}
